import React from 'react';
import type { EnrichedGoalNode, EnrichedObjectiveNode } from '../../services/hoshinDashboardData';
import {
  formatHoshinDate,
  isHoshinDatePast,
  MiniProgress,
  Td,
  Th,
  WorkStatus,
  workTone,
} from './hoshinShared';

export type HoshinSelection =
  | { kind: 'objective'; data: EnrichedObjectiveNode }
  | { kind: 'goal'; data: EnrichedGoalNode; objective: EnrichedObjectiveNode };

interface HoshinObjectivesTableProps {
  objectives: EnrichedObjectiveNode[];
  selectedId: string | null;
  onSelect: (selection: HoshinSelection) => void;
}

function alvoText(indicator: string | null | undefined): string {
  const text = indicator?.trim();
  return text ? text : 'Sem alvo';
}

function personLine(name: string, place?: string): string {
  const who = name && name !== '—' ? name : 'Sem responsável';
  const where = place && place !== '—' ? place : '';
  return where ? `${who} · ${where}` : who;
}

export const HoshinObjectivesTable: React.FC<HoshinObjectivesTableProps> = ({
  objectives,
  selectedId,
  onSelect,
}) => {
  if (objectives.length === 0) {
    return (
      <div className="rounded-lg border border-dashed border-slate-300 bg-white px-4 py-10 text-center dark:border-slate-600 dark:bg-slate-900">
        <p className="text-[13px] font-medium text-slate-800 dark:text-slate-100">Nenhum objetivo da diretoria</p>
        <p className="mt-1 text-[12px] text-slate-500">O botão Objetivo abre o norte. As metas de setor nascem a partir dele.</p>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {objectives.map((objective) => {
        const objectiveSelected = selectedId === objective.id;
        const deadline = objective.target_date;
        const actionCount = objective.linkedActions.length;
        const tone = workTone(objective.actualProgress, actionCount, deadline);
        const late = tone === 'late' || (isHoshinDatePast(deadline) && objective.actualProgress < 100);

        return (
          <section
            key={objective.id}
            className={`overflow-hidden rounded-lg border bg-white dark:bg-slate-900 ${
              objectiveSelected
                ? 'border-slate-400 dark:border-slate-500'
                : 'border-slate-200 dark:border-slate-700'
            }`}
          >
            <button
              type="button"
              onClick={() => onSelect({ kind: 'objective', data: objective })}
              className={`flex w-full items-start justify-between gap-4 border-l-2 px-3 py-3 text-left transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/50 ${
                objectiveSelected ? 'border-l-slate-800 bg-slate-50 dark:border-l-slate-200 dark:bg-slate-800/40' : 'border-l-transparent'
              }`}
            >
              <div className="min-w-0">
                <div className="text-[10px] font-semibold uppercase tracking-widest text-slate-400">Objetivo</div>
                <div className="mt-0.5 text-[13px] font-semibold leading-snug text-slate-900 dark:text-slate-100">
                  {objective.title}
                </div>
                <div className="mt-0.5 text-[11px] text-slate-500">
                  {personLine(objective.sponsorName, 'Diretoria')}
                  <span className="mx-1.5 text-slate-300">·</span>
                  {objective.goals.length} {objective.goals.length === 1 ? 'meta' : 'metas'}
                </div>
              </div>
              <div className="flex shrink-0 items-center gap-5 pt-0.5">
                <HeaderFact label="Prazo" value={formatHoshinDate(deadline)} warn={late} />
                <HeaderFact
                  label="Ações"
                  value={actionCount === 0 ? 'Nenhuma' : String(actionCount)}
                  warn={objective.overdueActions > 0}
                  hint={objective.overdueActions > 0 ? `${objective.overdueActions} atrasada${objective.overdueActions > 1 ? 's' : ''}` : undefined}
                />
                <div className="hidden sm:block">
                  <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400">Andamento</div>
                  <div className="mt-1">
                    {actionCount === 0 ? (
                      <span className="text-[11px] text-slate-400">Sem ação</span>
                    ) : (
                      <MiniProgress value={objective.actualProgress} />
                    )}
                  </div>
                </div>
              </div>
            </button>

            {objective.goals.length === 0 ? (
              <div className="border-t border-slate-100 px-3 py-3 text-[12px] text-slate-500 dark:border-slate-800">
                Nenhuma meta de setor. Selecione o objetivo para desdobrar.
              </div>
            ) : (
              <table className="w-full border-collapse text-left">
                <thead>
                  <tr>
                    <Th>Meta do setor</Th>
                    <Th>Alvo</Th>
                    <Th>Prazo</Th>
                    <Th>Ações</Th>
                    <Th>Andamento</Th>
                  </tr>
                </thead>
                <tbody>
                  {objective.goals.map((goal) => {
                    const goalDeadline = goal.target_date ?? objective.target_date;
                    const goalTone = workTone(goal.progress, goal.action_count, goalDeadline);
                    const goalLate = isHoshinDatePast(goalDeadline) && goal.progress < 100;
                    const selected = selectedId === goal.id;
                    const hasAlvo = Boolean(goal.target_indicator?.trim());

                    return (
                      <tr
                        key={goal.id}
                        onClick={() => onSelect({ kind: 'goal', data: goal, objective })}
                        className={`cursor-pointer transition-colors hover:bg-slate-50 dark:hover:bg-slate-800/40 ${
                          selected ? 'bg-slate-100 dark:bg-slate-800/70' : ''
                        }`}
                      >
                        <Td className={selected ? 'border-l-2 border-l-slate-800 dark:border-l-slate-200' : ''}>
                          <div className="font-medium text-slate-900 dark:text-slate-100">{goal.title}</div>
                          <div className="mt-0.5 text-[10px] text-slate-500">
                            {personLine(goal.ownerName, goal.departmentName)}
                          </div>
                        </Td>
                        <Td className={hasAlvo ? 'text-slate-800' : 'text-slate-400'}>
                          <span className="block max-w-[220px] leading-snug">{alvoText(goal.target_indicator)}</span>
                        </Td>
                        <Td className={goalLate ? 'font-medium text-red-600' : ''}>
                          {formatHoshinDate(goalDeadline)}
                        </Td>
                        <Td>
                          <span className="tabular-nums">{goal.action_count}</span>
                          {goal.overdueActions > 0 && (
                            <span className="ml-1 text-[10px] text-red-600">{goal.overdueActions} atras.</span>
                          )}
                        </Td>
                        <Td>
                          {goal.action_count === 0 ? (
                            <WorkStatus tone="empty" />
                          ) : (
                            <div className="flex flex-col gap-1">
                              <MiniProgress value={goal.progress} />
                              <WorkStatus tone={goalTone} />
                            </div>
                          )}
                        </Td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </section>
        );
      })}
    </div>
  );
};

function HeaderFact({
  label,
  value,
  hint,
  warn,
}: {
  label: string;
  value: string;
  hint?: string;
  warn?: boolean;
}) {
  return (
    <div className="text-right">
      <div className="text-[10px] font-medium uppercase tracking-wide text-slate-400">{label}</div>
      <div className={`mt-0.5 text-[12px] font-semibold tabular-nums ${warn ? 'text-red-600' : 'text-slate-800 dark:text-slate-100'}`}>
        {value}
      </div>
      {hint && <div className="text-[10px] text-red-600">{hint}</div>}
    </div>
  );
}
